import React, { useMemo } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import Select from 'react-select';
import 'bootstrap-daterangepicker/daterangepicker.css';

const selectCustomStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        borderColor: state.isFocused ? '#4f46e5' : '#cbd5e1',
        boxShadow: state.isFocused ? '0 0 0 1px #4f46e5' : 'none',
        minHeight: '38px',
        fontSize: '0.85rem',
        backgroundColor: '#fff'
    }),
    menuPortal: base => ({
        ...base,
        zIndex: 99999
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: '0.85rem',
        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : null,
        color: state.isSelected ? '#fff' : '#1e293b'
    })
};

const UserMarksFilterBar = React.memo(({
    dateString,
    onApplyDate,
    onSetAllTime,
    searchMode,
    searchTerm,
    onSearchChange,
    onSync,
    isLoading,
    groupLesson,
    onToggleGroupLesson,
    selectedClassTypeId,
    onClassTypeChange,
    classTypeOptions = [],
    scoreFilter,
    onScoreFilterChange,
    sortBy,
    onSortChange,
    quickTab,
    onQuickTabChange,
    showAdvancedSearch,
    onToggleAdvancedSearch,
    hasActiveFilters,
    onResetFilters,
    t
}) => {
    // Standard Date Ranges
    const ranges = useMemo(() => {
        let r = {};
        r[t("range.Today") || 'اليوم'] = [moment().toDate(), moment().toDate()];
        r[t("range.Yesterday") || 'الأمس'] = [moment().subtract(1, 'days'), moment().subtract(1, 'days')];
        r[t("range.Last7Days") || 'آخر 7 أيام'] = [moment().subtract(6, 'days'), moment()];
        r[t("range.Last30Days") || 'آخر 30 يوماً'] = [moment().subtract(29, 'days'), moment()];
        r[t("range.ThisMonth") || 'هذا الشهر'] = [moment().startOf('month'), moment().endOf('month')];
        r[t("range.LastMonth") || 'الشهر الماضي'] = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
        return r;
    }, [t]);

    const monthNames = useMemo(() => [
        t("months.January") || "يناير",
        t("months.February") || "فبراير",
        t("months.March") || "مارس",
        t("months.April") || "أبريل",
        t("months.May") || "مايو",
        t("months.June") || "يونيو",
        t("months.July") || "يوليو",
        t("months.August") || "أغسطس",
        t("months.September") || "سبتمبر",
        t("months.October") || "أكتوبر",
        t("months.November") || "نوفمبر",
        t("months.December") || "ديسمبر"
    ], [t]);

    const daysOfWeek = useMemo(() => [
        t("daysOfWeek.Sunday") || "الأحد",
        t("daysOfWeek.Monday") || "الاثنين",
        t("daysOfWeek.Tuesday") || "الثلاثاء",
        t("daysOfWeek.Wednesday") || "الأربعاء",
        t("daysOfWeek.Thursday") || "الخميس",
        t("daysOfWeek.Friday") || "الجمعة",
        t("daysOfWeek.Saturday") || "السبت"
    ], [t]);

    const scoreOptions = [
        { value: 'ALL', label: t('Pages.Lessons.AllScoreLevels') || 'جميع مستويات التحصيل' },
        { value: 'EXCELLENT', label: t('Pages.Lessons.ScoreExcellentFilter') || '🌟 ممتاز (90% فأعلى)' },
        { value: 'VERY_GOOD', label: t('Pages.Lessons.ScoreVeryGoodFilter') || '👍 جيد جداً (75% - 89%)' },
        { value: 'GOOD', label: t('Pages.Lessons.ScoreGoodFilter') || '⚡ متوسط (60% - 74%)' },
        { value: 'NEEDS_WORK', label: t('Pages.Lessons.ScoreNeedsWorkFilter') || '⚠️ بحاجة لتحسين (أقل من 60%)' }
    ];

    const sortOptions = [
        { value: 'DATE_DESC', label: t('Pages.Lessons.SortDateDesc') || '📅 الأحدث تاريخاً' },
        { value: 'DATE_ASC', label: t('Pages.Lessons.SortDateAsc') || '📅 الأقدم تاريخاً' },
        { value: 'SCORE_DESC', label: t('Pages.Lessons.SortScoreDesc') || '🏆 الأعلى علامة' },
        { value: 'SCORE_ASC', label: t('Pages.Lessons.SortScoreAsc') || '📉 الأقل علامة' },
        { value: 'ATTEMPTS_DESC', label: t('Pages.Lessons.SortAttemptsDesc') || '🔄 الأكثر محاولات' },
        { value: 'NAME_ASC', label: t('Pages.Lessons.SortNameAsc') || '🔤 أبجدياً (أ - ي)' }
    ];

    const selectedClassValue = classTypeOptions.find(opt => opt.value === selectedClassTypeId) || classTypeOptions[0];
    const selectedScoreValue = scoreOptions.find(opt => opt.value === scoreFilter) || scoreOptions[0];
    const selectedSortValue = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

    return (
        <div className="theme-card summary-card overflow-hidden bg-white p-3 p-md-4 mb-4 shadow-sm anim-fade-up">
            {/* 1. Main Row: Date Picker, Search Input, Search Action */}
            <div className="row align-items-center">
                {/* Date Picker */}
                <div className="col-lg-4 col-md-6 col-12 mb-3 mb-lg-0">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="font-weight-bold text-dark small d-flex align-items-center mb-0">
                            <i className="far fa-calendar-alt text-primary ml-1"></i>
                            <span>{t('SearchDate.title') || 'الفترة الزمنية'}</span>
                        </label>
                        <button
                            type="button"
                            className={`btn btn-link btn-sm p-0 small ${searchMode === 'all' ? 'text-success font-weight-bold' : 'text-muted'}`}
                            onClick={onSetAllTime}
                            style={{ fontSize: '0.78rem', width: 'auto' }}
                        >
                            <i className="fas fa-infinity ml-1"></i>
                            {searchMode === 'all' ? `${t('Pages.Lessons.AllTime') || 'كل الفترات'} (مفعّل)` : (t('Pages.Lessons.AllTime') || 'كل الفترات')}
                        </button>
                    </div>
                    <div className="position-relative">
                        <DateRangePicker
                            initialSettings={{
                                opens: 'center',
                                autoUpdateInput: false,
                                buttonClasses: 'btn',
                                cancelClass: "btn-danger",
                                applyButtonClasses: "btn-primary",
                                showDropdowns: true,
                                maxDate: `${moment().format('DD/MM/YYYY')}`,
                                ranges: ranges,
                                minYear: 2020,
                                locale: {
                                    format: 'DD/MM/YYYY',
                                    cancelLabel: t('Close') || 'إغلاق',
                                    firstDay: 6,
                                    applyLabel: t('Apply') || 'تطبيق',
                                    customRangeLabel: t('range.customRange') || 'فترة مخصصة',
                                    monthNames: monthNames,
                                    daysOfWeek: daysOfWeek
                                },
                            }}
                            onApply={onApplyDate}
                        >
                            <input
                                value={dateString}
                                readOnly
                                className="form-control"
                                style={{
                                    borderRadius: '8px',
                                    backgroundColor: searchMode === 'all' ? '#f0fdf4' : '#f8fafc',
                                    cursor: 'pointer',
                                    fontSize: '0.88rem',
                                    fontWeight: 'bold',
                                    color: searchMode === 'all' ? '#166534' : '#334155',
                                    borderColor: searchMode === 'all' ? '#86efac' : '#cbd5e1'
                                }}
                                title={t('SearchDate.title') || 'تاريخ العلامات'}
                            />
                        </DateRangePicker>
                    </div>
                </div>

                {/* Keyword Search Input */}
                <div className="col-lg-5 col-md-6 col-12 mb-3 mb-lg-0">
                    <label className="font-weight-bold mb-1 text-dark small d-flex align-items-center">
                        <i className="fas fa-search text-info ml-1"></i>
                        <span>{t('Pages.Lessons.search') || 'بحث باسم الدرس أو المادة'}</span>
                    </label>
                    <div className="position-relative">
                        <input
                            type="text"
                            className="form-control pl-4"
                            placeholder={t('Pages.Lessons.SearchPlaceholder') || 'اكتب اسم الدرس، الموضوع، أو المجال...'}
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            style={{
                                borderRadius: '8px',
                                fontSize: '0.88rem',
                                backgroundColor: '#f8fafc'
                            }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-link position-absolute text-muted p-0"
                                style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', width: 'auto' }}
                                onClick={() => onSearchChange('')}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Action Buttons & Grouping Toggle */}
                <div className="col-lg-3 col-12 d-flex align-items-center justify-content-lg-end pt-lg-4" style={{ gap: '8px' }}>
                    <button
                        type="button"
                        className="btn btn-primary font-weight-bold shadow-sm d-flex align-items-center justify-content-center flex-grow-1"
                        style={{
                            borderRadius: '8px',
                            minHeight: '38px',
                            fontSize: '0.85rem',
                            width: 'auto'
                        }}
                        onClick={onSync}
                        disabled={isLoading}
                    >
                        <i className={`fas fa-sync-alt ml-2 ${isLoading ? 'fa-spin' : ''}`}></i>
                        <span>{t('Pages.Lessons.searchButton') || t('Pages.Missions.searchButton') || 'تحديث وبحث'}</span>
                    </button>

                    <button
                        type="button"
                        className={`btn ${showAdvancedSearch ? 'btn-secondary' : 'btn-outline-secondary'} shadow-sm d-flex align-items-center justify-content-center`}
                        style={{
                            borderRadius: '8px',
                            minHeight: '38px',
                            minWidth: '42px',
                            fontSize: '0.9rem',
                            width: 'auto'
                        }}
                        onClick={onToggleAdvancedSearch}
                        title={showAdvancedSearch ? (t('Pages.Lessons.HideAdvancedSearch') || 'إخفاء التصفية المتقدمة') : (t('Pages.Lessons.ShowAdvancedSearch') || 'خيارات تصفية وترتيب إضافية')}
                    >
                        <i className="fas fa-sliders-h"></i>
                    </button>
                </div>
            </div>

            {/* 2. Grouping Option & Quick Filter Chips Row */}
            <div className="d-flex flex-wrap align-items-center justify-content-between pt-3 mt-3 border-top" style={{ gap: '8px' }}>
                {/* groupLesson Toggle */}
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span className="small font-weight-bold text-dark d-flex align-items-center">
                        <i className="fas fa-layer-group text-primary ml-1"></i>
                        <span>{t('Pages.Lessons.GroupLesson') || 'تجميع حسب الدرس'}:</span>
                    </span>
                    <button
                        type="button"
                        className={`btn btn-sm ${groupLesson ? 'btn-primary' : 'btn-light border'} px-3 font-weight-bold`}
                        style={{ borderRadius: '20px', fontSize: '0.78rem', width: 'auto' }}
                        onClick={onToggleGroupLesson}
                    >
                        <i className={`fas ${groupLesson ? 'fa-check-circle ml-1' : 'fa-list ml-1'}`}></i>
                        {groupLesson ? `${t('Pages.Lessons.GroupLesson') || 'تجميع المحاولات'} (${t('Pages.Lessons.Enabled') || 'مفعّل'})` : (t('Pages.Lessons.AllAttempts') || 'عرض المحاولات منفصلة')}
                    </button>
                </div>

                {/* Quick Filter Tabs */}
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
                    <span className="small text-muted ml-2">{t('Pages.Lessons.QuickFilter') || 'تصفية سريعة'}:</span>
                    <button
                        type="button"
                        className={`btn btn-sm ${quickTab === 'ALL' ? 'btn-dark' : 'btn-light border'} py-1 px-2`}
                        style={{ borderRadius: '6px', fontSize: '0.76rem', fontWeight: quickTab === 'ALL' ? 'bold' : 'normal', width: 'auto' }}
                        onClick={() => onQuickTabChange('ALL')}
                    >
                        {t('All') || 'الكل'}
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${quickTab === 'MASTERED' ? 'btn-success' : 'btn-light border'} py-1 px-2`}
                        style={{ borderRadius: '6px', fontSize: '0.76rem', fontWeight: quickTab === 'MASTERED' ? 'bold' : 'normal', width: 'auto' }}
                        onClick={() => onQuickTabChange('MASTERED')}
                    >
                        {t('Pages.Lessons.MasteredTab') || '🌟 المتقنة (≥80%)'}
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${quickTab === 'PRACTICING' ? 'btn-warning' : 'btn-light border'} py-1 px-2`}
                        style={{ borderRadius: '6px', fontSize: '0.76rem', fontWeight: quickTab === 'PRACTICING' ? 'bold' : 'normal', width: 'auto' }}
                        onClick={() => onQuickTabChange('PRACTICING')}
                    >
                        {t('Pages.Lessons.PracticingTab') || '⚡ قيد التدريب (50-79%)'}
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${quickTab === 'NEEDS_WORK' ? 'btn-danger' : 'btn-light border'} py-1 px-2`}
                        style={{ borderRadius: '6px', fontSize: '0.76rem', fontWeight: quickTab === 'NEEDS_WORK' ? 'bold' : 'normal', width: 'auto' }}
                        onClick={() => onQuickTabChange('NEEDS_WORK')}
                    >
                        {t('Pages.Lessons.NeedsWorkTab') || '⚠️ بحاجة لإعادة (<50%)'}
                    </button>
                </div>
            </div>

            {/* 3. Advanced Search & Filter Controls (Collapsible) */}
            {showAdvancedSearch && (
                <div className="mt-3 pt-3 border-top anim-rise">
                    <div className="row">
                        {/* Class Type Filter (1 to 9) */}
                        <div className="col-lg-4 col-md-6 col-12 mb-3">
                            <label className="font-weight-bold mb-1 text-dark small d-flex align-items-center">
                                <i className="fas fa-graduation-cap text-warning ml-1"></i>
                                <span>{t('Pages.Lessons.ClassTypeTitle') || 'الصف الدراسي'}</span>
                            </label>
                            <Select
                                value={selectedClassValue}
                                options={classTypeOptions}
                                onChange={(opt) => onClassTypeChange(opt ? opt.value : null)}
                                placeholder={t('Pages.Lessons.AllClasses') || 'جميع الصفوف الدراسية'}
                                isClearable={true}
                                styles={selectCustomStyles}
                                menuPortalTarget={document.body}
                                isRtl={true}
                                noOptionsMessage={() => t('Pages.Lessons.NoOptions') || t('NoOptions') || "لا توجد خيارات"}
                            />
                        </div>

                        {/* Performance / Score Filter */}
                        <div className="col-lg-4 col-md-6 col-12 mb-3">
                            <label className="font-weight-bold mb-1 text-dark small d-flex align-items-center">
                                <i className="fas fa-medal text-success ml-1"></i>
                                <span>{t('Pages.Lessons.AllScoreLevels') || 'مستوى الإتقان والعلامة'}</span>
                            </label>
                            <Select
                                value={selectedScoreValue}
                                options={scoreOptions}
                                onChange={(opt) => onScoreFilterChange(opt ? opt.value : 'ALL')}
                                styles={selectCustomStyles}
                                menuPortalTarget={document.body}
                                isRtl={true}
                            />
                        </div>

                        {/* Sort Order */}
                        <div className="col-lg-4 col-md-6 col-12 mb-3">
                            <label className="font-weight-bold mb-1 text-dark small d-flex align-items-center">
                                <i className="fas fa-sort-amount-down text-primary ml-1"></i>
                                <span>{t('Pages.Lessons.SortBy') || 'ترتيب النتائج حسب'}</span>
                            </label>
                            <Select
                                value={selectedSortValue}
                                options={sortOptions}
                                onChange={(opt) => onSortChange(opt ? opt.value : 'DATE_DESC')}
                                styles={selectCustomStyles}
                                menuPortalTarget={document.body}
                                isRtl={true}
                            />
                        </div>
                    </div>

                    {/* Reset Active Filters Button */}
                    {hasActiveFilters && (
                        <div className="d-flex justify-content-end mt-1">
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm px-3 py-1 font-weight-bold"
                                style={{ borderRadius: '6px', fontSize: '0.80rem', width: 'auto' }}
                                onClick={onResetFilters}
                            >
                                <i className="fas fa-undo ml-1"></i>
                                <span>{t('Pages.Lessons.ResetFilters') || 'إعادة تعيين كافة الفلاتر'}</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default UserMarksFilterBar;
